<template>
  <v-container>
    <div>
      <v-btn class="mb-3" @click="goBack" prepend-icon="mdi-arrow-left" text="Back to List" />
    </div>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title class="d-flex justify-space-between align-center">
            <span>Manage Prompts</span>
          </v-card-title>

          <v-card-text>
            <v-row>
              <!-- Description Prompts Section -->
              <v-col cols="12" md="6">
                <v-card variant="outlined">
                  <v-card-title
                    class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center gap-2 py-3"
                  >
                    <span class="text-h6">Description Prompts</span>
                    <v-btn
                      color="grey-darken-1"
                      variant="text"
                      size="small"
                      prepend-icon="mdi-plus"
                      @click="openAddPromptDialog('description')"
                      class="align-self-end align-self-sm-center"
                    >
                      Add Prompt
                    </v-btn>
                  </v-card-title>

                  <v-card-text>
                    <div v-if="promptsStore.isLoading" class="text-center py-4">
                      <v-progress-circular indeterminate />
                      <div class="mt-2">Loading prompts...</div>
                    </div>

                    <div
                      v-else-if="promptsStore.descriptionPrompts.length === 0"
                      class="text-center py-4 text-grey"
                    >
                      No description prompts found. Create your first prompt to get started.
                    </div>

                    <v-list v-else>
                      <v-list-item
                        v-for="prompt in promptsStore.descriptionPrompts"
                        :key="prompt.id"
                        class="border rounded mb-2"
                      >
                        <v-list-item-title>{{ prompt.name }}</v-list-item-title>
                        <v-list-item-subtitle class="text-wrap">
                          {{ prompt.content.substring(0, 100)
                          }}{{ prompt.content.length > 100 ? '...' : '' }}
                        </v-list-item-subtitle>

                        <template #append>
                          <div class="d-flex gap-1">
                            <v-btn
                              icon="mdi-pencil"
                              size="small"
                              variant="text"
                              @click="editPrompt(prompt)"
                            />
                            <v-btn
                              icon="mdi-delete"
                              size="small"
                              variant="text"
                              color="error"
                              @click="deletePrompt(prompt)"
                            />
                          </div>
                        </template>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Highlight Prompts Section -->
              <v-col cols="12" md="6">
                <v-card variant="outlined">
                  <v-card-title
                    class="d-flex flex-column flex-sm-row justify-space-between align-start align-sm-center gap-2 py-3"
                  >
                    <span class="text-h6">Highlight Prompts</span>
                    <v-btn
                      color="grey-darken-1"
                      variant="text"
                      size="small"
                      prepend-icon="mdi-plus"
                      @click="openAddPromptDialog('highlight')"
                      class="align-self-end align-self-sm-center"
                    >
                      Add Prompt
                    </v-btn>
                  </v-card-title>

                  <v-card-text>
                    <div v-if="promptsStore.isLoading" class="text-center py-4">
                      <v-progress-circular indeterminate />
                      <div class="mt-2">Loading prompts...</div>
                    </div>

                    <div
                      v-else-if="promptsStore.highlightPrompts.length === 0"
                      class="text-center py-4 text-grey"
                    >
                      No highlight prompts found. Create your first prompt to get started.
                    </div>

                    <v-list v-else>
                      <v-list-item
                        v-for="prompt in promptsStore.highlightPrompts"
                        :key="prompt.id"
                        class="border rounded mb-2"
                      >
                        <v-list-item-title>{{ prompt.name }}</v-list-item-title>
                        <v-list-item-subtitle class="text-wrap">
                          {{ prompt.content.substring(0, 100)
                          }}{{ prompt.content.length > 100 ? '...' : '' }}
                        </v-list-item-subtitle>

                        <template #append>
                          <div class="d-flex gap-1">
                            <v-btn
                              icon="mdi-pencil"
                              size="small"
                              variant="text"
                              @click="editPrompt(prompt)"
                            />
                            <v-btn
                              icon="mdi-delete"
                              size="small"
                              variant="text"
                              color="error"
                              @click="deletePrompt(prompt)"
                            />
                          </div>
                        </template>
                      </v-list-item>
                    </v-list>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Add/Edit Prompt Dialog -->
    <v-dialog v-model="showPromptDialog" max-width="600px">
      <v-card>
        <v-card-title>
          {{ editingPrompt ? 'Edit Prompt' : 'Add New Prompt' }}
        </v-card-title>

        <v-card-text>
          <v-form v-model="isFormValid" @submit.prevent="savePrompt">
            <v-text-field
              v-model="promptData.name"
              label="Prompt Name"
              :rules="[(v: string) => !!v || 'Name is required']"
              variant="outlined"
              class="mb-3"
            />

            <v-textarea
              v-model="promptData.content"
              label="Prompt Content"
              :rules="[(v: string) => !!v || 'Content is required']"
              variant="outlined"
              rows="6"
              class="mb-3"
            />

            <v-select
              v-model="promptData.type"
              :items="promptTypes"
              label="Prompt Type"
              :rules="[(v: string) => !!v || 'Type is required']"
              variant="outlined"
              :disabled="!!editingPrompt"
            />

            <v-alert
              v-if="promptData.type === 'highlight'"
              type="info"
              class="mt-3"
              density="compact"
            >
              Highlight prompts will automatically format output as bullet points for the SRV
              (researcher homepage).
            </v-alert>
          </v-form>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text="Cancel" @click="closePromptDialog" />
          <v-btn
            color="primary"
            text="Save"
            :loading="promptsStore.isLoading"
            :disabled="!isFormValid"
            @click="savePrompt"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Confirm Delete</v-card-title>
        <v-card-text>
          Are you sure you want to delete the prompt "{{ promptToDelete?.name }}"? This action
          cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text="Cancel" @click="showDeleteDialog = false" />
          <v-btn
            color="error"
            text="Delete"
            :loading="promptsStore.isLoading"
            @click="confirmDelete"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Error Snackbar -->
    <v-snackbar v-model="showError" color="error" timeout="5000">
      {{ errorMessage }}
    </v-snackbar>

    <!-- Success Snackbar -->
    <v-snackbar v-model="showSuccess" color="success" timeout="3000">
      {{ successMessage }}
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { ref, watch, nextTick } from 'vue'
import { usePromptsStore, type IPrompt } from '@/stores/prompts'

// Props
interface Props {
  goBack?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  goBack: undefined,
})

// Stores and router
const promptsStore = usePromptsStore()

// Dialog states
const showPromptDialog = ref(false)
const showDeleteDialog = ref(false)
const showError = ref(false)
const showSuccess = ref(false)

// Form states
const isFormValid = ref(false)
const editingPrompt = ref<IPrompt | null>(null)
const promptToDelete = ref<IPrompt | null>(null)

// Messages
const errorMessage = ref('')
const successMessage = ref('')

// Form data
// const promptForm = ref()

// Form data
const promptData = ref({
  name: '',
  content: '',
  type: 'description' as 'description' | 'highlight',
})

// Form options
const promptTypes = [
  { title: 'Description', value: 'description' },
  { title: 'Highlight', value: 'highlight' },
]

// Watch for errors from the store
watch(
  () => promptsStore.error,
  (error) => {
    if (error) {
      errorMessage.value = error
      showError.value = true
    }
  },
)

// Navigation
const goBack = () => {
  if (props.goBack) {
    props.goBack()
  }
}

// Methods
const openAddPromptDialog = async (type: 'description' | 'highlight') => {
  editingPrompt.value = null

  // Ensure promptForm is properly initialized
  if (!promptData.value) {
    promptData.value = {
      name: '',
      content: '',
      type: 'description',
    }
  }

  // Use individual assignments to ensure reactivity
  promptData.value.name = ''
  promptData.value.content = ''
  promptData.value.type = type

  await nextTick()
  showPromptDialog.value = true
}

const editPrompt = async (prompt: IPrompt) => {
  editingPrompt.value = prompt

  // Ensure promptForm is properly initialized
  if (!promptData.value) {
    promptData.value = {
      name: '',
      content: '',
      type: 'description',
    }
  }

  // Use individual assignments and nextTick to ensure reactivity
  promptData.value.name = prompt.name
  promptData.value.content = prompt.content
  promptData.value.type = prompt.type

  await nextTick()
  showPromptDialog.value = true
}

const closePromptDialog = () => {
  showPromptDialog.value = false
  editingPrompt.value = null

  // Ensure promptForm is properly initialized
  if (!promptData.value) {
    promptData.value = {
      name: '',
      content: '',
      type: 'description',
    }
    return
  }

  // Reset form using individual assignments
  promptData.value.name = ''
  promptData.value.content = ''
  promptData.value.type = 'description'
}

const savePrompt = async () => {
  if (!isFormValid.value) return

  try {
    const content = promptData.value.content

    if (editingPrompt.value) {
      // Update existing prompt
      await promptsStore.updatePrompt(editingPrompt.value.id, {
        name: promptData.value.name,
        content,
        type: promptData.value.type,
      })
      successMessage.value = 'Prompt updated successfully!'
    } else {
      // Create new prompt
      await promptsStore.addPrompt({
        name: promptData.value.name,
        content,
        type: promptData.value.type,
      })
      successMessage.value = 'Prompt created successfully!'
    }

    showSuccess.value = true
    closePromptDialog()
  } catch (error) {
    console.error('Failed to save prompt:', error)
    errorMessage.value = 'Failed to save prompt. Please try again.'
    showError.value = true
  }
}

const deletePrompt = (prompt: IPrompt) => {
  promptToDelete.value = prompt
  showDeleteDialog.value = true
}

const confirmDelete = async () => {
  if (!promptToDelete.value) return

  try {
    await promptsStore.removePrompt(promptToDelete.value.id, promptToDelete.value.type)
    successMessage.value = 'Prompt deleted successfully!'
    showSuccess.value = true
    showDeleteDialog.value = false
    promptToDelete.value = null
  } catch (error) {
    console.error('Failed to delete prompt:', error)
    errorMessage.value = 'Failed to delete prompt. Please try again.'
    showError.value = true
  }
}
</script>

<style scoped>
.border {
  border: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
